import { useState, useEffect, useRef } from 'react'
import { Phone, PhoneOff, Mic, MicOff, Delete, Hash, Circle } from 'lucide-react'
import useSipStore from '../stores/sipStore'
import useHistoryStore from '../stores/historyStore'

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
]
const VALID_KEYS = new Set(['0','1','2','3','4','5','6','7','8','9','*','#'])

function formatDuration(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, '0')
  const s = (sec % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

export default function Dialpad() {
  const status = useSipStore(s => s.status)
  const inCall = useSipStore(s => s.inCall)
  const isRinging = useSipStore(s => s.isRinging)
  const isMuted = useSipStore(s => s.isMuted)
  const callerNumber = useSipStore(s => s.callerNumber)
  const callDirection = useSipStore(s => s.callDirection)
  const callStartTime = useSipStore(s => s.callStartTime)
  const dial = useSipStore(s => s.dial)
  const answer = useSipStore(s => s.answer)
  const hangup = useSipStore(s => s.hangup)
  const setMuted = useSipStore(s => s.setMuted)
  const sendDtmf = useSipStore(s => s.sendDtmf)
  const recordByDefault = useSipStore(s => s.recordByDefault)
  const setRecordByDefault = useSipStore(s => s.setRecordByDefault)
  const addHistory = useHistoryStore(s => s.add)
  const updateHistory = useHistoryStore(s => s.update)

  const [number, setNumber] = useState('')
  const [duration, setDuration] = useState(0)
  const [currentCallId, setCurrentCallId] = useState(null)
  const [pressedKey, setPressedKey] = useState(null)  // for keyboard visual feedback
  const handleDialRef = useRef(null)
  const pressRef = useRef(null)

  useEffect(() => {
    if (!callStartTime) { setDuration(0); return }
    const id = setInterval(() => {
      setDuration(Math.floor((Date.now() - callStartTime) / 1000))
    }, 500)
    return () => clearInterval(id)
  }, [callStartTime])

  // Track call in history
  useEffect(() => {
    if (isRinging && callDirection === 'in' && !currentCallId) {
      const id = addHistory({ direction: 'in', number: callerNumber, status: 'ringing' })
      setCurrentCallId(id)
    }
  }, [isRinging, callDirection, callerNumber])

  useEffect(() => {
    if (inCall && currentCallId) {
      updateHistory(currentCallId, { status: 'answered' })
    }
  }, [inCall])

  useEffect(() => {
    if (!inCall && !isRinging && currentCallId) {
      updateHistory(currentCallId, { status: 'ended', endedAt: new Date().toISOString(), durationSec: duration })
      setCurrentCallId(null)
    }
  }, [inCall, isRinging])

  const press = (k) => {
    if (inCall) {
      sendDtmf(k)
    } else {
      setNumber(n => n + k)
    }
    // Visual feedback: highlight the matching keypad button briefly
    setPressedKey(k)
    setTimeout(() => setPressedKey(p => (p === k ? null : p)), 120)
  }
  pressRef.current = press

  const handleDial = () => {
    if (!number.trim()) return
    const id = addHistory({
      direction: 'out',
      number: number.trim(),
      status: 'ringing',
      recorded: recordByDefault,
    })
    setCurrentCallId(id)
    try {
      dial(number.trim(), { record: recordByDefault })
    } catch (e) {
      updateHistory(id, { status: 'failed', cause: e.message })
      setCurrentCallId(null)
    }
  }
  handleDialRef.current = handleDial

  const canDial = status === 'registered' && number.trim().length >= 3

  // Global keyboard input - works even when no input is focused.
  // - 0-9, *, # → enter digit (or send DTMF if in call)
  // - Enter   → dial / answer
  // - Backspace → delete last digit (only when not in call)
  // - Escape → hangup
  // - + → adds + for international (only when not in call, before dialing)
  useEffect(() => {
    const onKey = (e) => {
      // Don't hijack when the user is typing in a real input/textarea/contenteditable
      const tag = (e.target?.tagName || '').toLowerCase()
      const isEditable = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable
      if (isEditable) return

      // Don't capture keys with modifiers (let browser shortcuts work)
      if (e.ctrlKey || e.metaKey || e.altKey) return

      // Digit / * / # from any keyboard layout (event.key is the resulting char)
      if (VALID_KEYS.has(e.key)) {
        e.preventDefault()
        pressRef.current(e.key)
        return
      }

      // Numpad symbols come through as event.key too, but explicitly handle:
      if (e.key === 'Enter') {
        e.preventDefault()
        if (isRinging && callDirection === 'in') answer()
        else if (!inCall && canDial) handleDialRef.current()
      } else if (e.key === 'Backspace' && !inCall && !isRinging) {
        e.preventDefault()
        setNumber(n => n.slice(0, -1))
      } else if (e.key === 'Escape') {
        if (inCall || isRinging) {
          e.preventDefault()
          hangup()
        }
      } else if (e.key === '+' && !inCall) {
        e.preventDefault()
        setNumber(n => n + '+')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [inCall, isRinging, callDirection, canDial, answer, hangup])

  return (
    <div className="bg-panel border border-line rounded-2xl p-6 max-w-sm mx-auto">
      {/* Display area */}
      <div className="mb-4 text-center">
        {inCall || isRinging ? (
          <div>
            <div className="text-mute text-sm mb-1">
              {isRinging && callDirection === 'in' ? 'שיחה נכנסת מ' :
               callDirection === 'out' ? 'מחייג ל' : 'בשיחה עם'}
            </div>
            <div className="text-3xl font-semibold tech ltr">{callerNumber || '?'}</div>
            {inCall && (
              <div className="text-xl tech text-brand mt-1 flex items-center justify-center gap-2">
                {formatDuration(duration)}
                {recordByDefault && callDirection === 'out' && (
                  <span title="הקלטה פעילה" className="flex items-center gap-1 text-sm text-rose-400">
                    <Circle size={10} className="fill-rose-500 text-rose-500 animate-pulse" />
                    REC
                  </span>
                )}
              </div>
            )}
            {isRinging && callDirection === 'out' && (
              <div className="text-sm text-mute mt-1 animate-pulse">…מצלצל</div>
            )}
          </div>
        ) : (
          <div className="relative">
            <input
              type="tel"
              value={number}
              onChange={(e) => setNumber(e.target.value.replace(/[^0-9*#+]/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleDial()}
              placeholder="הקלד מספר"
              className="w-full text-center text-3xl tech ltr bg-transparent border-0 focus:outline-none placeholder:text-mute/40 py-2"
            />
            {number && (
              <button
                onClick={() => setNumber(n => n.slice(0, -1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-mute hover:text-text"
              >
                <Delete size={20} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Record toggle (only visible when not in call) */}
      {!inCall && !isRinging && (
        <button
          onClick={() => setRecordByDefault(!recordByDefault)}
          className={`w-full mb-3 px-3 py-2 rounded-xl border flex items-center justify-between
            transition-colors text-sm
            ${recordByDefault
              ? 'bg-rose-500/10 border-rose-500/40 text-rose-300'
              : 'bg-line/20 border-line text-mute hover:text-text'
            }`}
        >
          <span className="flex items-center gap-2">
            <Circle size={12} className={recordByDefault ? 'fill-rose-500 text-rose-500 animate-pulse' : ''} />
            הקלט שיחה
          </span>
          <span className="text-xs">{recordByDefault ? 'פעיל' : 'כבוי'}</span>
        </button>
      )}

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {KEYS.flat().map(k => (
          <button
            key={k}
            onClick={() => press(k)}
            className={`aspect-square rounded-2xl active:scale-95
              transition-all text-2xl font-medium tech flex items-center justify-center
              ${pressedKey === k ? 'bg-brand/30 scale-95' : 'bg-line/30 hover:bg-line/60'}`}
          >
            {k}
          </button>
        ))}
      </div>

      {/* Actions */}
      {isRinging && callDirection === 'in' ? (
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={answer}
            className="py-3 rounded-xl bg-ok hover:bg-ok/80 text-slate-900 font-semibold
              flex items-center justify-center gap-2"
          >
            <Phone size={20} /> ענה
          </button>
          <button
            onClick={hangup}
            className="py-3 rounded-xl bg-err hover:bg-err/80 text-white font-semibold
              flex items-center justify-center gap-2"
          >
            <PhoneOff size={20} /> דחה
          </button>
        </div>
      ) : inCall ? (
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => setMuted(!isMuted)}
            className={`py-3 rounded-xl font-semibold flex items-center justify-center gap-1
              ${isMuted ? 'bg-warn text-slate-900' : 'bg-line/30 hover:bg-line/60'}`}
          >
            {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
          <button
            disabled
            className="py-3 rounded-xl bg-line/20 text-mute flex items-center justify-center gap-1"
          >
            <Hash size={18} />
          </button>
          <button
            onClick={hangup}
            className="py-3 rounded-xl bg-err hover:bg-err/80 text-white flex items-center justify-center gap-1"
          >
            <PhoneOff size={18} />
          </button>
        </div>
      ) : (
        <button
          onClick={handleDial}
          disabled={!canDial}
          className="w-full py-4 rounded-xl bg-brand hover:bg-brand/90 disabled:bg-line/30
            disabled:text-mute text-slate-900 font-bold text-lg flex items-center justify-center gap-2
            transition-colors"
        >
          <Phone size={20} /> חייג
        </button>
      )}

      {/* Keyboard shortcut hints */}
      {!inCall && !isRinging && (
        <div className="mt-3 text-[11px] text-mute text-center ltr">
          <span className="inline-block">
            <kbd className="px-1.5 py-0.5 rounded bg-line/40 tech">0-9 * #</kbd> ספרות
          </span>
          {' · '}
          <span className="inline-block">
            <kbd className="px-1.5 py-0.5 rounded bg-line/40 tech">Enter</kbd> חייג
          </span>
          {' · '}
          <span className="inline-block">
            <kbd className="px-1.5 py-0.5 rounded bg-line/40 tech">⌫</kbd> מחק
          </span>
        </div>
      )}
      {(inCall || isRinging) && (
        <div className="mt-3 text-[11px] text-mute text-center ltr">
          {inCall && <><kbd className="px-1.5 py-0.5 rounded bg-line/40 tech">0-9 * #</kbd> DTMF · </>}
          {isRinging && callDirection === 'in' && <><kbd className="px-1.5 py-0.5 rounded bg-line/40 tech">Enter</kbd> ענה · </>}
          <kbd className="px-1.5 py-0.5 rounded bg-line/40 tech">Esc</kbd> נתק
        </div>
      )}
    </div>
  )
}
